import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Paper, Typography, Button, FormControl, InputLabel, Select, MenuItem, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Divider, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { fetchProfitAndLoss } from '../redux/actions/reportActions';

const safeFormatCurrency = (value) => {
  const num = parseFloat(value || 0);
  if (Number.isNaN(num)) return '0.00';
  return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const ProfitAndLoss = () => {
  const dispatch = useDispatch();
  const { pl, loading, error } = useSelector(state => state.reports);
  const [basis, setBasis] = useState('accrual');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d;
  });
  const [endDate, setEndDate] = useState(() => new Date());

  useEffect(() => {
    const start = startDate ? format(startDate, 'yyyy-MM-dd') : undefined;
    const end = endDate ? format(endDate, 'yyyy-MM-dd') : undefined;
    dispatch(fetchProfitAndLoss(basis, { startDate: start, endDate: end }));
  }, [dispatch, basis, startDate, endDate]);

  const handleRun = () => {
    const start = startDate ? format(startDate, 'yyyy-MM-dd') : undefined;
    const end = endDate ? format(endDate, 'yyyy-MM-dd') : undefined;
    dispatch(fetchProfitAndLoss(basis, { startDate: start, endDate: end }));
  };

  const summary = pl?.summary || {};
  const revenueByCategory = pl?.revenueByCategory || [];
  const expensesByCategory = pl?.expensesByCategory || [];
  const cashInByMode = pl?.cashInByMode || [];
  const cashOutByCategory = pl?.cashOutByCategory || [];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Profit & Loss</Typography>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Basis</InputLabel>
                <Select value={basis} label="Basis" onChange={(e) => setBasis(e.target.value)}>
                  <MenuItem value="accrual">Accrual</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <DatePicker label="Start Date" value={startDate} onChange={setStartDate} renderInput={(params) => <TextField fullWidth size="small" {...params} />} />
            </Grid>
            <Grid item xs={12} md={3}>
              <DatePicker label="End Date" value={endDate} onChange={setEndDate} renderInput={(params) => <TextField fullWidth size="small" {...params} />} />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button variant="contained" onClick={handleRun}>Run</Button>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2">{basis === 'cash' ? 'Cash In' : 'Revenue'}</Typography>
              <Typography variant="h5">₹{safeFormatCurrency(summary.revenue || summary.cashIn || 0)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2">{basis === 'cash' ? 'Cash Out' : 'Expenses'}</Typography>
              <Typography variant="h5">₹{safeFormatCurrency(summary.expenses || summary.cashOut || 0)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2">Net</Typography>
              <Typography variant="h5">₹{safeFormatCurrency(summary.netProfit || summary.netCash || 0)}</Typography>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {basis === 'accrual' ? (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 1 }}>Revenue by Category</Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {revenueByCategory.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{row.category || 'N/A'}</TableCell>
                        <TableCell align="right">₹{safeFormatCurrency(row.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 1 }}>Expenses by Category</Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {expensesByCategory.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{row['category.name'] || row.category_name || 'N/A'}</TableCell>
                        <TableCell align="right">₹{safeFormatCurrency(row.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 1 }}>Cash In by Mode</Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Mode</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cashInByMode.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{row.payment_mode_code || 'N/A'}</TableCell>
                        <TableCell align="right">₹{safeFormatCurrency(row.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 1 }}>Cash Out by Category</Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cashOutByCategory.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{row['expense.category.code'] || row['expense.category.name'] || 'N/A'}</TableCell>
                        <TableCell align="right">₹{safeFormatCurrency(row.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default ProfitAndLoss;


